import { getService, setService, createService } from '@/services';
import { debounce, flattenArray, schedule, timeInSeconds } from '@/utils';
import { createListenerStore } from '@/listeners';

import createRuleStore from './createRuleStore';
import * as conditionTests from './ruleConditionTests';
import { conditionValueIsEmpty, tryService, handleDivPlacement, checkDivs, randomPercentage } from './utils';
import { showAlwaysPerSessionTriggers, showOncePerSessionTriggers } from './constants';

export default function createRule({ rule = {}, globalListenerStore } = {}) {
	const { id, if: ifRules, then } = rule;

	const ruleChangeListeners = [];

	const {
		setState,
		setConditionState,
		getState,
		getConditionState,
		setValidState,
		getValidState,
		getValidConditions,
		setTimeOnPageState,
		getTimeOnPageState,
	} = createRuleStore(rule);

	const { clearListeners, setListener, setIntervalListener } = createListenerStore();

	const preCheckEffects = {
		percentage: ({ index }) => {
			if (getConditionState(index, state => state.percentage) === undefined) {
				setConditionState(index, state => (state.percentage = randomPercentage()));
			}
		},
		time_in_session: ({ index, condition }) => {
			const isValid = () => getConditionState(index, state => state.sessionTime) >= Number(condition.time_in_session);
			if (isValid()) {
				return;
			}
			setIntervalListener(() => {
				setConditionState(index, conditionState => {
					conditionState.sessionTime =
						typeof conditionState.sessionTime !== 'undefined' ? conditionState.sessionTime + 1 : 1;
				});
				if (isValid()) {
					checkConditions();
				}
			}, 1000);
		},
		number_of_pages: () => {
			setState(state => {
				if (state.lastPage !== document.location.href) {
					state.pageCount = typeof state.pageCount !== 'undefined' ? state.pageCount + 1 : 1;
				}
				state.lastPage = document.location.href;
			});
		},
		type: () => {
			if (getState(state => state.returning)) {
				return;
			}

			if (timeInSeconds() - globalListenerStore.getSession() < 120) {
				setState(state => {
					state.returning = true;
				});
			}
		},
		event_listeners: ({ index, value }) => {
			try {
				flattenArray(value).forEach((eventListener, eventListenerIndex) => {
					const { selector, event } = eventListener;

					if (getConditionState(index, state => state.domEvents?.[selector])?.indexOf(event) > -1) {
						console.log('DOM event already fired, not creating listener...');
						return;
					}

					setListener(listeners => {
						const listenerKey = event + selector + index + eventListenerIndex;
						console.log('assigning listener', listenerKey);
						listeners[listenerKey] = e => {
							setTimeout(() => {
								console.log('event fired...', listenerKey, selector);
								try {
									if (e.target?.matches(selector)) {
										setConditionState(index, state => {
											if (!state.domEvents) {
												state.domEvents = {};
											}
											if (!state.domEvents[selector]) {
												state.domEvents[selector] = [];
											}
											state.domEvents[selector].push(event);
										});
										checkConditions();
									}
								} catch (e) {}
							});
						};

						document.addEventListener(event, listeners[listenerKey]);
						return () => document.removeEventListener(event, listeners[listenerKey]);
					});
				});
			} catch (e) {}
		},
		form_events: ({ index, value, service: serviceName }) => {
			try {
				if (!getService(serviceName)) {
					setService(serviceName, createService(serviceName));
				}
				const service = getService(serviceName);

				flattenArray(value).forEach((formListener, formListenerIndex) => {
					const { formKey, event } = formListener;

					if (getConditionState(index, state => state.formEvents?.[formKey])?.indexOf(event) > -1) {
						console.log(`Form event ${event} for ${formKey} already fired, not creating listener...`);
						return;
					}

					const applyListener = () => {
						setListener(listeners => {
							console.log('setting listener', formKey, event, index, formListenerIndex);
							const listenerKey = formKey + event + index + formListenerIndex;
							listeners[listenerKey] = e => {
								setTimeout(() => {
									console.log('event fired...', listenerKey, e);
									if (e.key === formKey) {
										console.log('setting form event', formKey, event);
										setConditionState(index, state => {
											if (!state.formEvents[formKey]) {
												state.formEvents[formKey] = [];
											}
											state.formEvents[formKey].push(e.event);
										});
										checkConditions();
									}
								});
							};

							//timeout fixes quirk where addEventListener is called before service is properly initialized
							service.addEventListener(event, listeners[listenerKey]);
							return () => service.removeEventListener(event, listeners[listenerKey]);
						});
					};

					if (service.isLoaded()) {
						applyListener();
					} else {
						service.onLoaded(applyListener);
					}
				});
			} catch (e) {}
		},
	};

	function jsCookieCssSelectorEffect({ key, value, wasValid }) {
		setIntervalListener(() => {
			const conditionTestFn = conditionTests[key];
			const isValid = conditionTestFn(value, rule);
			if (wasValid && !isValid) {
				checkConditions();
			}
			if (!wasValid && isValid) {
				checkConditions();
			}
		});
	}

	const postCheckEffects = {
		js: jsCookieCssSelectorEffect,
		cookie: jsCookieCssSelectorEffect,
		css_selector: jsCookieCssSelectorEffect,
		scrollpos: ({ key, value, wasValid }) => {
			setListener(listeners => {
				listeners.scroll = debounce(() => {
					const conditionTestFn = conditionTests[key];
					const isValid = conditionTestFn(value);
					if (wasValid && !isValid) {
						checkConditions();
					}
					if (!wasValid && isValid) {
						checkConditions();
					}
				}, 50);
				window.addEventListener('scroll', listeners.scroll);

				return () => window.removeEventListener('scroll', listeners.scroll);
			});
		},
		time: ({ index, value }) => {
			const isValid = () => {
				return timeInSeconds() - getTimeOnPageState(index, 'time') >= Number(value);
			};
			if (!getTimeOnPageState(index, 'time')) {
				setTimeOnPageState(index, 'time');
			}
			if (isValid()) {
				return;
			}

			setIntervalListener(() => {
				if (isValid()) {
					checkConditions();
				}
			}, 1000);
		},
		time_after_valid: ({ index, value }) => {
			const isValid = () => {
				return timeInSeconds() - getTimeOnPageState(index, 'time_after_valid') >= Number(value);
			};
			if (!getTimeOnPageState(index, 'time_after_valid')) {
				setTimeOnPageState(index, 'time_after_valid');
			}
			if (isValid()) {
				return;
			}

			setIntervalListener(() => {
				if (isValid()) {
					checkConditions();
				}
			}, 1000);
		},
	};

	function validConditionsChanged(previous = []) {
		const previouslyValidConditions = previous.filter(({ index }) => index);
		return getValidConditions().every(({ index } = {}) => previouslyValidConditions.indexOf(index) === -1);
	}

	const conditionCheckOrder = [
		'pause',
		'location',
		'time',
		'percentage',
		'number_of_pages',
		'referrer',
		'date',
		'clock',
		'type',
		'devices',
		'css_selector',
		'js',
		'cookie',
		'scrollpos',
		'time_in_session',
		'event_listeners',
		'form_events',
		'time_after_valid',
	];

	function checkConditions() {
		schedule(() => {
			clearListeners();

			const previouslyValidConditions = getValidConditions();

			console.log('checking conditions for', id);

			const ruleConditionsValid =
				ifRules.filter((condition, index) => {
					console.log('---- condition', index + 1, '----');

					if (
						showOncePerSessionTriggers.indexOf(condition.trigger) > -1 &&
						getConditionState(index, state => state.triggered)
					) {
						console.log('already triggered, aborting', condition.trigger);
						return false;
					}

					//sets effects that should initiate before checking conditions
					Object.keys(preCheckEffects).forEach(conditionKey => {
						const conditionValue = condition[conditionKey];
						const preCheckEffect = preCheckEffects[conditionKey];
						if (!conditionValueIsEmpty(conditionValue) && preCheckEffect) {
							preCheckEffect({ index, condition, value: conditionValue, service: then?.[0]?.service });
						}
					});

					const isValid = conditionCheckOrder.every(conditionKey => {
						const conditionTestFn = conditionTests[conditionKey];
						const conditionValue = condition[conditionKey];

						const postCheckEffect = postCheckEffects[conditionKey];
						if (conditionValueIsEmpty(conditionValue)) {
							return true;
						}

						if (typeof conditionTestFn !== 'function') {
							return true;
						}
						const valid = conditionTestFn(conditionValue, {
							rule,
							state: {
								pageCount: getState(state => state.pageCount),
								returning: getState(state => state.returning),
								percentage: getConditionState(index, state => state.percentage),
								timeOnPage: getTimeOnPageState(index, 'time'),
								timeAfterValid: getTimeOnPageState(index, 'time_after_valid'),
								sessionTime: getConditionState(index, state => state.sessionTime),
								eventListeners: getConditionState(index, state => state.domEvents),
								formEvents: getConditionState(index, state => state.formEvents),
								referrer: globalListenerStore.getPreviousLocation(),
							},
						});

						console.log(conditionKey, conditionValue, valid);

						if (postCheckEffect) {
							postCheckEffect({
								key: conditionKey,
								value: conditionValue,
								wasValid: valid,
								index,
								service: then?.[0]?.service,
							});
						}

						console.log(conditionKey, valid ? 'valid' : 'not valid');

						return valid;
					});

					console.log('condition', index + 1, 'valid', isValid);
					setValidState(index, isValid);

					return isValid;
				}).length > 0;

			const divPlacementValid = handleDivPlacement(then, ruleConditionsValid);

			setIntervalListener(() => {
				if (divPlacementValid !== checkDivs(then)) {
					if (ruleConditionsValid) {
						//a condition was valid but now it's not anymore
						getValidConditions().forEach(({ index }) => {
							setValidState(index, false);
						});
					}
					checkConditions();
				}
			});

			console.log('div', divPlacementValid);

			const ruleIsCurrentlyValid = ruleConditionsValid && divPlacementValid;
			const conditionsChanged = validConditionsChanged(previouslyValidConditions);

			if (conditionsChanged && ruleIsCurrentlyValid) {
				console.log(`rule ${id} conditions changed to valid`);
				return runThen();
			} else if (previouslyValidConditions.length > 0 && !ruleIsCurrentlyValid) {
				console.log(`rule ${id} conditions changed to invalid`);
				return cleanupThen();
			}

			if (ruleIsCurrentlyValid) {
				console.log(`rule ${id} is still valid`);
			}

			if (!ruleIsCurrentlyValid) {
				console.log(`rule ${id} not valid`);
			}

			ruleChangeListeners.forEach(fn => fn({ ruleIsCurrentlyValid, conditionsChanged }));
		});
	}

	function runThen() {
		then.forEach((thenCondition = {}) => {
			const { service: serviceName = 'mopinion', useQuerySelectorAll, args = [] } = thenCondition;

			const [key, domain, divName] = args;

			//init service if it doesnt exist yet
			if (!getService(serviceName)) {
				setService(serviceName, createService(serviceName));
			}
			const service = getService(serviceName);
			const conditions = getValidConditions();

			const open = ({ index, condition }) => {
				tryService(() => {
					if (!getValidState(index)) {
						console.log('rule no longer valid on open');
						return;
					}

					if (
						showAlwaysPerSessionTriggers.indexOf(condition.trigger) > -1 ||
						!getConditionState(index, state => state.triggered)
					) {
						console.log(`handling ${condition.trigger} trigger for ${key}`);
						service.handleTrigger({
							key,
							domain,
							divName,
							useQuerySelectorAll,
							...condition,
							getTrail: globalListenerStore.getTrail,
						});
						setConditionState(index, state => (state.triggered = true));
					}
				});
			};
			service.start(() => {
				handleTriggers({
					conditions,
					id,
					open,
				});
			});
		});
	}

	function cleanupThen(cleanAll = false) {
		then.forEach(({ service: serviceName, args = [] } = {}) => {
			try {
				const service = getService(serviceName);
				console.log(`clearing ${args[0]} for service ${serviceName}`);
				service.removeWhenClosed(args[0]);
			} catch (e) {}

			clearListeners(cleanAll);
		});
	}

	function handleTriggers({ conditions = [], id, open }) {
		conditions.forEach(({ index, condition }) => {
			if (
				showOncePerSessionTriggers.indexOf(condition.trigger) > -1 &&
				getConditionState(index, state => state.triggered)
			) {
				console.log('already triggered, aborting');
				return;
			}

			setTimeout(() => {
				const initTrigger = () => {
					console.log('executing trigger', condition.trigger);
					open({ index, condition });
					clearListeners(false);
				};

				if (condition.trigger === 'exit') {
					return handleExit({
						condition,
						initTrigger,
					});
				}
				initTrigger();
			});
		});
	}

	function handleExit({ condition, initTrigger }) {
		const minY = 100;
		let mouseY = 0;

		console.log('exit trigger');
		setListener(listeners => {
			listeners.mousemove = e => (mouseY = e.clientY);
			listeners.mouseleave = e => {
				if (mouseY <= minY) {
					const YonLeave = mouseY;
					console.log('page leave detected');
					//check if mouse is _still_ outside of the window
					setTimeout(() => {
						if (mouseY === YonLeave) {
							console.log('page leave definitive');
							initTrigger();
							clearListeners(false);
						}
					}, 200);
				}
			};

			document.addEventListener('mousemove', listeners.mousemove);
			document.querySelector('html').addEventListener('mouseleave', listeners.mouseleave);

			return () => {
				document.removeEventListener('mousemove', listeners.mousemove);
				document.querySelector('html').removeEventListener('mouseleave', listeners.mouseleave);
			};
		});
	}

	function onRuleChange(fn) {
		if (typeof fn !== 'function') {
			return;
		}
		ruleChangeListeners.push(fn);
	}

	return {
		checkConditions,
		cleanupThen,
		getState,
		getConditionState,
		getValidState,
		getValidConditions,
		getTimeOnPageState,
		onRuleChange,
		globalListenerStore,
	};
}
